import { FC, Fragment, useCallback } from 'react';
import { TagCloud, TagCloudItem } from '@cp/ds/src/components/tagCloud';
import { useClientWebResolver, useClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { useCurrentCity } from '@cp/entities/location';
import { coreCatalogApi } from '@cp/entities/catalog';
import { CatalogItemCareerTagCloudsResponse } from '@common/model/catalog/item/countings.request';
import { useRouter } from '@cp/shared/router';
import { CatalogCareerTagCloudsProps } from './types';

export const CatalogCareerTagClouds: FC<CatalogCareerTagCloudsProps> = ({ type, sx }) => {
  const router = useRouter();
  const routeResolver = useClientWebResolver();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const { pageProps } = useClientWebRouteParams<ClientWebPage.Catalog>();
  const { data: tagClouds, isLoading } = coreCatalogApi.endpoints.getItemsCareerTagClouds.useQuery({ cityAlias: currentCity!.alias });
  const hasTagClouds = tagClouds && !isLoading;
  const mutate = useCallback(
    (clouds: CatalogItemCareerTagCloudsResponse, catalogType: CatalogItemType): TagCloudItem[] =>
      clouds[catalogType].map<TagCloudItem>(({ careerViewModel, hidden }) => {
        const url = routeResolver
          .resolve({
            page: ClientWebPage.Catalog,
            cityAlias: currentCity!.alias,
            pageProps: { catalogType, careerId: careerViewModel.id },
          })
          .toString();
        return {
          key: careerViewModel.id,
          label: careerViewModel.translation.namePlural,
          ...(!hidden || (Number(careerViewModel.id) === pageProps.careerId && pageProps.catalogType === catalogType)
            ? {
                onClick: () => {
                  router.navigate(url);
                },
              }
            : {
                href: url,
              }),
        };
      }),
    [pageProps.careerId, pageProps.catalogType, routeResolver, currentCity, router],
  );

  if (!type) {
    return (
      <Fragment>
        {hasTagClouds ? (
          <TagCloud items={mutate(tagClouds, CatalogItemType.Vacancy)} sx={sx} title="Вакансии" />
        ) : (
          <ContentSkeleton blocks={3} />
        )}
        {hasTagClouds ? (
          <TagCloud items={mutate(tagClouds, CatalogItemType.Resume)} sx={sx} title="Резюме" />
        ) : (
          <ContentSkeleton blocks={3} />
        )}
      </Fragment>
    );
  }

  const title = type === CatalogItemType.Resume ? 'Резюме' : 'Вакансии';
  return hasTagClouds ? <TagCloud items={mutate(tagClouds, type)} sx={sx} title={title} /> : <ContentSkeleton blocks={3} />;
};
